const ASSISTANT_TRANSPORT_VALUE_KEY = '__videocutTransportValue'

type EncodedTransportValue =
  | {
      [ASSISTANT_TRANSPORT_VALUE_KEY]: 'Blob'
      type: string
      dataBase64: string
    }
  | {
      [ASSISTANT_TRANSPORT_VALUE_KEY]: 'File'
      name: string
      type: string
      lastModified: number
      dataBase64: string
    }

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') {
    return false
  }

  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function isEncodedTransportValue(value: unknown): value is EncodedTransportValue {
  if (!isPlainObject(value)) {
    return false
  }

  return (
    value[ASSISTANT_TRANSPORT_VALUE_KEY] === 'Blob' ||
    value[ASSISTANT_TRANSPORT_VALUE_KEY] === 'File'
  )
}

function toBase64(bytes: Uint8Array): string {
  const globalBuffer = (globalThis as { Buffer?: { from: typeof Buffer.from } }).Buffer
  if (globalBuffer) {
    return globalBuffer.from(bytes).toString('base64')
  }

  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary)
}

function fromBase64(value: string): Uint8Array {
  const globalBuffer = (globalThis as { Buffer?: { from: typeof Buffer.from } }).Buffer
  if (globalBuffer) {
    return Uint8Array.from(globalBuffer.from(value, 'base64'))
  }

  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

async function serializeBlob(blob: Blob): Promise<EncodedTransportValue> {
  const bytes = new Uint8Array(await readBlobBytes(blob))
  return {
    [ASSISTANT_TRANSPORT_VALUE_KEY]: 'Blob',
    type: blob.type,
    dataBase64: toBase64(bytes),
  }
}

async function serializeFile(file: File): Promise<EncodedTransportValue> {
  const bytes = new Uint8Array(await readBlobBytes(file))
  return {
    [ASSISTANT_TRANSPORT_VALUE_KEY]: 'File',
    name: file.name,
    type: file.type,
    lastModified: file.lastModified,
    dataBase64: toBase64(bytes),
  }
}

async function readBlobBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') {
    return await blob.arrayBuffer()
  }

  if (typeof FileReader !== 'undefined') {
    return await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result)
          return
        }

        reject(new Error('Expected FileReader to produce an ArrayBuffer result.'))
      }
      reader.onerror = () => {
        reject(reader.error ?? new Error('Failed to read Blob contents.'))
      }
      reader.readAsArrayBuffer(blob)
    })
  }

  return await new Response(blob).arrayBuffer()
}

export async function serializeAssistantTransportValue(value: unknown): Promise<unknown> {
  if (value instanceof File) {
    return await serializeFile(value)
  }

  if (value instanceof Blob) {
    return await serializeBlob(value)
  }

  if (Array.isArray(value)) {
    return await Promise.all(value.map((entry) => serializeAssistantTransportValue(entry)))
  }

  if (isEncodedTransportValue(value)) {
    return value
  }

  if (isPlainObject(value)) {
    const entries = await Promise.all(
      Object.entries(value).map(async ([key, entry]) => [
        key,
        await serializeAssistantTransportValue(entry),
      ] as const),
    )
    return Object.fromEntries(entries)
  }

  return value
}

export function deserializeAssistantTransportValue<T = unknown>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((entry) => deserializeAssistantTransportValue(entry)) as T
  }

  if (isEncodedTransportValue(value)) {
    const bytes = fromBase64(value.dataBase64)
    if (value[ASSISTANT_TRANSPORT_VALUE_KEY] === 'File') {
      return new File([bytes], value.name, {
        type: value.type,
        lastModified: value.lastModified,
      }) as T
    }

    return new Blob([bytes], { type: value.type }) as T
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, deserializeAssistantTransportValue(entry)]),
    ) as T
  }

  return value
}
